import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Image as ImageIcon, Package, CheckCircle2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

/* ---------- SVG Templates ---------- */
// 512x512 App Icon SVG (Play Store requirement)
const iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#059669"/>
      <stop offset="0.55" stop-color="#0891b2"/>
      <stop offset="1" stop-color="#7c3aed"/>
    </linearGradient>
    <linearGradient id="crown" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#fde68a"/>
      <stop offset="1" stop-color="#f59e0b"/>
    </linearGradient>
    <filter id="soft" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="6"/>
    </filter>
  </defs>
  <rect width="512" height="512" rx="112" ry="112" fill="url(#bg)"/>
  <circle cx="256" cy="256" r="220" fill="#ffffff" opacity="0.06"/>
  <circle cx="120" cy="120" r="140" fill="#ffffff" opacity="0.05"/>
  <!-- Diya (traditional lamp) icon -->
  <ellipse cx="256" cy="330" rx="140" ry="42" fill="#78350f"/>
  <path d="M120 320 Q256 240 392 320 L360 340 Q256 300 152 340 Z" fill="#f97316"/>
  <path d="M256 220 Q248 170 256 130 Q264 170 256 220" fill="url(#crown)" opacity="0.95"/>
  <circle cx="256" cy="150" r="34" fill="#fde68a" opacity="0.6" filter="url(#soft)"/>
  <!-- Odia character 'ଗୁ' (Gu) - stylized as monogram -->
  <text x="256" y="150" text-anchor="middle" font-family="serif" font-size="120" font-weight="900" fill="#ffffff" opacity="0.0">ଗୁ</text>
  <!-- Book / knowledge symbol -->
  <g transform="translate(196, 380)">
    <rect x="0" y="0" width="120" height="12" rx="2" fill="#ffffff" opacity="0.9"/>
    <rect x="18" y="16" width="84" height="8" rx="2" fill="#ffffff" opacity="0.7"/>
  </g>
  <!-- "G" letter mark -->
  <text x="256" y="290" text-anchor="middle" font-family="Georgia, serif" font-size="180" font-weight="900" fill="#ffffff">G</text>
</svg>`;

// 1024x500 Feature Graphic (Play Store banner)
const featureSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="1024" height="500" viewBox="0 0 1024 500">
  <defs>
    <linearGradient id="fbg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#059669"/>
      <stop offset="0.5" stop-color="#0891b2"/>
      <stop offset="1" stop-color="#7c3aed"/>
    </linearGradient>
    <radialGradient id="glow" cx="0.8" cy="0.5" r="0.6">
      <stop offset="0" stop-color="#fde68a" stop-opacity="0.35"/>
      <stop offset="1" stop-color="#fde68a" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="1024" height="500" fill="url(#fbg)"/>
  <rect width="1024" height="500" fill="url(#glow)"/>
  <!-- decorative circles -->
  <circle cx="90" cy="80" r="60" fill="#ffffff" opacity="0.08"/>
  <circle cx="180" cy="420" r="90" fill="#ffffff" opacity="0.06"/>
  <circle cx="900" cy="450" r="120" fill="#ffffff" opacity="0.07"/>

  <!-- Icon block on left -->
  <g transform="translate(60, 130)">
    <rect x="0" y="0" width="200" height="200" rx="46" ry="46" fill="#ffffff" opacity="0.16"/>
    <rect x="14" y="14" width="172" height="172" rx="38" ry="38" fill="#ffffff"/>
    <text x="100" y="150" text-anchor="middle" font-family="Georgia, serif" font-size="150" font-weight="900" fill="#059669">G</text>
  </g>

  <!-- Text block -->
  <g transform="translate(300, 130)">
    <text x="0" y="55" font-family="Arial, sans-serif" font-size="62" font-weight="900" fill="#ffffff">Gurusevakendra</text>
    <text x="0" y="105" font-family="Arial, sans-serif" font-size="28" font-weight="700" fill="#fde68a">ଆପଣଙ୍କ ସଫଳତାର ସାଥୀ</text>
    <text x="0" y="150" font-family="Arial, sans-serif" font-size="22" font-weight="500" fill="#ffffff" opacity="0.95">Navodaya · 10th Board · Teacher</text>
    <text x="0" y="185" font-family="Arial, sans-serif" font-size="20" font-weight="500" fill="#ffffff" opacity="0.85">Odia notes · AI Doubt · Quiz · PDF</text>

    <!-- Feature pills -->
    <g transform="translate(0, 215)">
      <rect x="0" y="0" width="130" height="34" rx="17" fill="#ffffff" opacity="0.22"/>
      <text x="65" y="22" text-anchor="middle" font-family="Arial" font-size="15" font-weight="700" fill="#ffffff">📚 Notes</text>
      <rect x="140" y="0" width="140" height="34" rx="17" fill="#ffffff" opacity="0.22"/>
      <text x="210" y="22" text-anchor="middle" font-family="Arial" font-size="15" font-weight="700" fill="#ffffff">🤖 AI Chat</text>
      <rect x="290" y="0" width="120" height="34" rx="17" fill="#ffffff" opacity="0.22"/>
      <text x="350" y="22" text-anchor="middle" font-family="Arial" font-size="15" font-weight="700" fill="#ffffff">🎯 Quiz</text>
    </g>
  </g>

  <!-- Right corner decorative -->
  <g transform="translate(820, 60)">
    <text x="0" y="0" font-family="Arial" font-size="80" opacity="0.25">📖</text>
  </g>
  <g transform="translate(880, 340)">
    <text x="0" y="0" font-family="Arial" font-size="60" opacity="0.30">🎓</text>
  </g>
</svg>`;

// 192x192 icon for PWA (adaptive)
const icon192Svg = iconSvg.replace('width="512" height="512"', 'width="192" height="192"');
// 96x96
const icon96Svg = iconSvg.replace('width="512" height="512"', 'width="96" height="96"');

/* ---------- Helpers ---------- */
async function svgToPngBlob(svgString, w, h) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      // white bg fallback so PNG is opaque where needed
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (blob) resolve(blob); else reject(new Error('toBlob failed'));
      }, 'image/png');
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/* ---------- Component ---------- */
const AssetCard = ({ title, size, filename, svg, w, h, testid }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    try {
      setBusy(true);
      const blob = await svgToPngBlob(svg, w, h);
      downloadBlob(blob, filename);
      toast({ title: 'Downloaded ✅', description: `${filename} (${w}×${h} PNG)` });
    } catch (e) {
      toast({ title: 'Error', description: 'PNG ତିଆରି ହୋଇ ପାରିଲା ନାହିଁ। ଦୟାକରି browser refresh କରନ୍ତୁ।' });
    } finally { setBusy(false); }
  };
  // Make SVG scalable in preview (remove fixed width/height so viewBox handles scaling)
  const previewSvg = svg.replace(/width="\d+"\s+height="\d+"/, 'width="100%" height="100%" preserveAspectRatio="xMidYMid meet"');
  return (
    <Card className="rounded-2xl p-4 border-gray-200">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-bold text-sm text-gray-900">{title}</div>
          <div className="text-[11px] text-gray-500">{size} · PNG</div>
        </div>
        <ImageIcon size={20} className="text-emerald-500" />
      </div>
      <div className="bg-gray-100 rounded-xl p-2 mb-3 overflow-hidden" style={{ aspectRatio: `${w}/${h}` }}>
        <img src={`data:image/svg+xml;utf8,${encodeURIComponent(previewSvg)}`} alt="asset preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
      <Button onClick={download} disabled={busy} data-testid={testid}
        className="w-full h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs">
        <Download size={14} className="mr-1" /> {busy ? 'Preparing...' : `Download PNG`}
      </Button>
    </Card>
  );
};

export default function AssetsPage() {
  const [zipBusy, setZipBusy] = useState(false);

  const downloadAll = async () => {
    try {
      setZipBusy(true);
      const files = [
        { svg: iconSvg, w: 512, h: 512, name: 'gurusevakendra-icon-512.png' },
        { svg: iconSvg, w: 192, h: 192, name: 'gurusevakendra-icon-192.png' },
        { svg: iconSvg, w: 96, h: 96, name: 'gurusevakendra-icon-96.png' },
        { svg: featureSvg, w: 1024, h: 500, name: 'gurusevakendra-feature-1024x500.png' },
      ];
      for (const f of files) {
        const blob = await svgToPngBlob(f.svg, f.w, f.h);
        downloadBlob(blob, f.name);
        await new Promise(r => setTimeout(r, 400));
      }
      toast({ title: '୪ ଟି file download ହୋଇଛି ✅', description: 'Downloads folder ଚେକ୍ କରନ୍ତୁ' });
    } catch (e) {
      toast({ title: 'Error', description: 'Please try individual downloads' });
    } finally { setZipBusy(false); }
  };

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium" data-testid="assets-back">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #059669 0%, #0891b2 60%, #7c3aed 100%)' }}>
        <Package size={26} />
        <h1 className="text-xl font-extrabold mt-2">🎨 Play Store Assets</h1>
        <p className="text-xs text-white/90 mt-1">Google Play ପାଇଁ ସମସ୍ତ image ଏଠାରୁ Download କରନ୍ତୁ</p>
      </div>

      <Card className="rounded-2xl p-3 border-blue-200 bg-blue-50">
        <div className="flex items-start gap-2">
          <div className="text-lg">💡</div>
          <p className="text-[11px] text-blue-800 leading-relaxed">
            <strong>ବ୍ୟବହାର:</strong> ପ୍ରତ୍ୟେକ button ଉପରେ tap କରନ୍ତୁ → PNG file download ହେବ → Google Play Console ର Store Listing ରେ upload କରନ୍ତୁ। ଏକଥର ସବୁ ଡାଉନଲୋଡ୍ କରିବାକୁ ତଳେ "All Download" button ଉପରେ click କରନ୍ତୁ।
          </p>
        </div>
      </Card>

      <Button onClick={downloadAll} disabled={zipBusy} data-testid="download-all-btn"
        className="w-full h-12 rounded-2xl bg-gradient-to-r from-emerald-600 to-purple-600 hover:opacity-90 text-white font-extrabold text-base">
        <Download size={18} className="mr-2" /> {zipBusy ? 'Preparing all files...' : '⚡ All Assets Download (4 files)'}
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <AssetCard title="App Icon (Play Store)" size="512 × 512" filename="gurusevakendra-icon-512.png" svg={iconSvg} w={512} h={512} testid="download-icon-512" />
        <AssetCard title="App Icon (PWA)" size="192 × 192" filename="gurusevakendra-icon-192.png" svg={iconSvg} w={192} h={192} testid="download-icon-192" />
        <AssetCard title="App Icon (Small)" size="96 × 96" filename="gurusevakendra-icon-96.png" svg={iconSvg} w={96} h={96} testid="download-icon-96" />
        <AssetCard title="Feature Graphic" size="1024 × 500" filename="gurusevakendra-feature-1024x500.png" svg={featureSvg} w={1024} h={500} testid="download-feature" />
      </div>

      {/* Upload instructions */}
      <Card className="rounded-2xl p-4 border-emerald-200 bg-emerald-50">
        <div className="font-bold text-emerald-800 mb-2 flex items-center gap-2"><CheckCircle2 size={16}/> Play Console ରେ କେଉଁଠି Upload କରିବେ?</div>
        <ul className="space-y-1.5 text-xs text-emerald-900">
          <li>• <strong>App Icon (512):</strong> Play Console → Main Store Listing → App icon</li>
          <li>• <strong>Feature Graphic (1024×500):</strong> Play Console → Main Store Listing → Feature graphic</li>
          <li>• <strong>Icon 192/96:</strong> ଏଗୁଡ଼ିକ PWA / TWA manifest ପାଇଁ (developer ପାଇଁ)</li>
          <li>• <strong>Screenshots:</strong> ଆପଣଙ୍କ phone ରୁ App ର ୨-୮ ଟି screenshot ନିଅନ୍ତୁ (min 1080×1920)</li>
        </ul>
      </Card>

      {/* Screenshots instructions */}
      <Card className="rounded-2xl p-4 border-amber-200 bg-amber-50">
        <div className="font-bold text-amber-800 mb-2">📸 Screenshots ନେବା ପାଇଁ Tip</div>
        <ol className="space-y-1.5 text-xs text-amber-900 list-decimal pl-5">
          <li>Chrome ମୋବାଇଲ୍ ରୁ App URL ଖୋଲନ୍ତୁ</li>
          <li>Home page, Navodaya, Board, Chat, Payment page ର screenshot ନିଅନ୍ତୁ</li>
          <li>Volume Down + Power ଏକସାଥେ press କରି screenshot ନିଅନ୍ତୁ</li>
          <li>ସର୍ବନିମ୍ନ ୨ଟି, ସର୍ବାଧିକ ୮ଟି upload କରନ୍ତୁ</li>
        </ol>
      </Card>

      <p className="text-center text-[10px] text-gray-500">
        Design: Gurusevakendra · Emerald + Cyan + Purple brand gradient
      </p>
    </div>
  );
}
